import React, { useEffect, useId, useRef, useState } from "react";

import type { Patient } from "./patient_search";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 200;

interface DesktopPatientSearchProps {
  patientSearchUrl: string;
  documentSearchUrl: string;
}

interface DocumentResult {
  id: number | string;
  title: string;
  documentPath: string;
  documentTarget?: string | null;
  documentRel?: string | null;
  patientName?: string | null;
  kindLabel?: string | null;
  sourceLabel?: string | null;
  reviewStatus?: string | null;
  receivedOn?: string | null;
  contentTypeLabel?: string | null;
  summary?: string | null;
}

interface ParsedQuery {
  commaSeparated?: boolean;
  patient?: string | null;
  document?: string | null;
}

interface NavbarPatientSearchResponse {
  patients?: Patient[];
  parsedQuery?: ParsedQuery;
  documentSearchPath?: string | null;
}

interface NavbarDocumentSearchResponse {
  documents?: DocumentResult[];
  documentSearchPath?: string | null;
}

function patientDemographics(patient: Patient): string {
  const details = [`DOB ${patient.dateOfBirth || "Unknown"}`];

  if (patient.age != null) {
    details.push(`Age ${patient.age}`);
  }

  return details.join(" | ");
}

function patientCreatedLabel(patient: Patient): string {
  return `Created ${patient.createdOn || "Unknown"}`;
}

function patientEmailLabel(patient: Patient): string {
  return patient.primaryEmail || "No email";
}

function CalendarIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function OperationIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
      viewBox="0 0 24 24"
    >
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

function documentMeta(document: DocumentResult): string {
  return [
    document.patientName || "Unlinked",
    document.receivedOn ? `Received ${document.receivedOn}` : null,
    document.reviewStatus || null,
    document.sourceLabel || null,
  ].filter((value): value is string => Boolean(value)).join(" | ");
}

function documentSecondaryText(document: DocumentResult): string {
  return document.summary || document.contentTypeLabel || "Document";
}

function documentChipLabel(document: DocumentResult): string {
  return document.kindLabel || "Document";
}

function parsedQueryActive(parsedQuery: ParsedQuery): boolean {
  return Boolean(parsedQuery.commaSeparated && (parsedQuery.patient || parsedQuery.document));
}

function useNavbarSearch(patientSearchUrl: string, documentSearchUrl: string) {
  const [query, setQuery] = useState("");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [documents, setDocuments] = useState<DocumentResult[]>([]);
  const [parsedQuery, setParsedQuery] = useState<ParsedQuery>({});
  const [documentSearchPath, setDocumentSearchPath] = useState<string | null>(null);
  const [patientSearchCompletedQuery, setPatientSearchCompletedQuery] = useState("");
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [documentErrorMessage, setDocumentErrorMessage] = useState("");
  const trimmedQuery = query.trim();
  const patientIds = patients.map((patient) => String(patient.id));
  const patientIdsKey = patientIds.join(",");

  useEffect(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setPatients([]);
      setDocuments([]);
      setParsedQuery({});
      setDocumentSearchPath(null);
      setPatientSearchCompletedQuery("");
      setLoadingPatients(false);
      setLoadingDocuments(false);
      setErrorMessage("");
      setDocumentErrorMessage("");
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setLoadingPatients(true);
      setLoadingDocuments(false);
      setPatients([]);
      setDocuments([]);
      setPatientSearchCompletedQuery("");
      setErrorMessage("");
      setDocumentErrorMessage("");

      try {
        const response = await fetch(`${patientSearchUrl}?q=${encodeURIComponent(trimmedQuery)}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Search failed with status ${response.status}`);
        }

        const payload: NavbarPatientSearchResponse = await response.json();
        setPatients(Array.isArray(payload.patients) ? payload.patients : []);
        setParsedQuery(payload.parsedQuery || {});
        setDocumentSearchPath(payload.documentSearchPath || null);
        setPatientSearchCompletedQuery(trimmedQuery);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") return;

        setPatients([]);
        setDocuments([]);
        setParsedQuery({});
        setDocumentSearchPath(null);
        setPatientSearchCompletedQuery("");
        setErrorMessage("Search is unavailable right now.");
      } finally {
        setLoadingPatients(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [patientSearchUrl, trimmedQuery]);

  useEffect(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH || patientSearchCompletedQuery !== trimmedQuery) {
      return undefined;
    }

    if (patientIds.length === 0) {
      setDocuments([]);
      setLoadingDocuments(false);
      setDocumentErrorMessage("");
      return undefined;
    }

    const controller = new AbortController();

    const fetchDocuments = async () => {
      setLoadingDocuments(true);
      setDocuments([]);
      setDocumentErrorMessage("");

      try {
        const params = new URLSearchParams({ q: trimmedQuery });
        patientIds.forEach((patientId) => params.append("patient_ids[]", patientId));

        const response = await fetch(`${documentSearchUrl}?${params.toString()}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Document search failed with status ${response.status}`);
        }

        const payload: NavbarDocumentSearchResponse = await response.json();
        setDocuments(Array.isArray(payload.documents) ? payload.documents : []);
        setDocumentSearchPath(payload.documentSearchPath || null);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") return;

        setDocuments([]);
        setDocumentErrorMessage("Document search is unavailable right now.");
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();

    return () => {
      controller.abort();
    };
  }, [documentSearchUrl, patientIdsKey, patientSearchCompletedQuery, trimmedQuery]);

  return {
    documentSearchPath,
    documentErrorMessage,
    documents,
    errorMessage,
    loadingDocuments,
    loadingPatients,
    parsedQuery,
    patients,
    query,
    setQuery,
    trimmedQuery,
  };
}

export default function DesktopPatientSearch({ patientSearchUrl, documentSearchUrl }: DesktopPatientSearchProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const {
    documentSearchPath,
    documentErrorMessage,
    documents,
    errorMessage,
    loadingDocuments,
    loadingPatients,
    parsedQuery,
    patients,
    query,
    setQuery,
    trimmedQuery,
  } = useNavbarSearch(patientSearchUrl, documentSearchUrl);
  const showDropdown = open;
  const showResults = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const hasResults = patients.length > 0 || documents.length > 0;
  const showParsedQuery = showResults && parsedQueryActive(parsedQuery);
  const searchFinished = showResults && !loadingPatients && !loadingDocuments;

  useEffect(() => {
    if (!showDropdown) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      if (containerRef.current?.contains(event.target)) return;

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      setOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDropdown]);

  return (
    <div ref={containerRef} className="relative" data-patient-navbar-search-root>
      <label htmlFor={listboxId} className="sr-only">
        Search patients and documents
      </label>

      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-neutral-400 dark:text-neutral-500">
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        </span>

        <input
          ref={inputRef}
          id={listboxId}
          type="search"
          value={query}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Escape") {
              setOpen(false);
              event.currentTarget.blur();
            }
          }}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={showDropdown ? `${listboxId}-results` : undefined}
          aria-expanded={showDropdown}
          autoComplete="off"
          placeholder="Search patients or documents"
          className="app-input mt-0 pl-10"
        />

        {showDropdown ? (
          <div
            id={`${listboxId}-results`}
            role="listbox"
            className="app-desktop-search-dropdown"
            style={{
              left: "auto",
              maxHeight: "min(42rem, calc(100vh - 5rem))",
              overflowX: "hidden",
              overflowY: "auto",
              width: "min(36rem, calc(100vw - 2rem))",
            }}
          >
            <div className="border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
              <span className="font-medium text-neutral-700 dark:text-neutral-200">Tip:</span>{" "}
              use a comma for document searches, like <span className="font-medium text-neutral-700 dark:text-neutral-200">Smith, sestamibi</span>.
            </div>

            {showParsedQuery ? (
              <div className="flex flex-wrap gap-1.5 border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
                {parsedQuery.patient ? (
                  <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                    Patient: {parsedQuery.patient}
                  </span>
                ) : null}
                {parsedQuery.document ? (
                  <span className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200">
                    Document: {parsedQuery.document}
                  </span>
                ) : null}
              </div>
            ) : null}

            {!showResults ? (
              <p className="px-3 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                Search by patient name, phone, email, filename, document summary.
              </p>
            ) : null}

            {showResults && loadingPatients ? (
              <p className="px-3 py-3 text-sm text-neutral-500 dark:text-neutral-400">Searching patients...</p>
            ) : null}

            {showResults && !loadingPatients && errorMessage ? (
              <p className="px-3 py-3 text-sm text-rose-600 dark:text-rose-300">{errorMessage}</p>
            ) : null}

            {searchFinished && !errorMessage && !documentErrorMessage && !hasResults ? (
              <p className="px-3 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                No matching patients or documents found.
              </p>
            ) : null}

            {showResults && !errorMessage && patients.length > 0 ? (
              <div className="border-b border-neutral-200 bg-slate-50 px-3 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
                Patients
              </div>
            ) : null}

            {showResults && !errorMessage
              ? patients.map((patient) => (
                  <div
                    key={`patient-${patient.id}`}
                    className="relative border-t border-neutral-200 px-3 py-3 first:border-t-0 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
                  >
                    <a
                      href={patient.patientPath}
                      role="option"
                      aria-selected="false"
                      aria-label={`Open ${patient.fullName}`}
                      className="absolute inset-0 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500"
                    />

                    <div className="pointer-events-none relative flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-neutral-950 dark:text-white">{patient.fullName}</p>
                        <p className="mt-1 truncate text-xs text-neutral-500 dark:text-neutral-400">
                          {patientDemographics(patient)}
                        </p>
                        <p className="mt-1 truncate text-sm text-neutral-600 dark:text-neutral-300">{patientEmailLabel(patient)}</p>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="whitespace-nowrap text-xs text-neutral-500 dark:text-neutral-400">
                          {patientCreatedLabel(patient)}
                        </p>

                        {patient.consultSchedulingPath || patient.operationSchedulingPath ? (
                          <div className="pointer-events-auto relative z-10 mt-1.5 flex justify-end gap-1">
                            {patient.consultSchedulingPath ? (
                              <a
                                href={patient.consultSchedulingPath}
                                aria-label={`Schedule a consult for ${patient.fullName}`}
                                title="Schedule consult"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                              >
                                <CalendarIcon />
                              </a>
                            ) : null}

                            {patient.operationSchedulingPath ? (
                              <a
                                href={patient.operationSchedulingPath}
                                aria-label={`Schedule an operation for ${patient.fullName}`}
                                title="Schedule operation"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                              >
                                <OperationIcon />
                              </a>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              : null}

            {showResults && !loadingPatients && !errorMessage && (documents.length > 0 || loadingDocuments || documentErrorMessage) ? (
              <div className="border-y border-neutral-200 bg-slate-50 px-3 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
                Documents
              </div>
            ) : null}

            {showResults && !loadingPatients && !errorMessage && loadingDocuments ? (
              <p className="px-3 py-3 text-sm text-neutral-500 dark:text-neutral-400">Searching documents...</p>
            ) : null}

            {showResults && !loadingPatients && !errorMessage && !loadingDocuments && documentErrorMessage ? (
              <p className="px-3 py-3 text-sm text-rose-600 dark:text-rose-300">{documentErrorMessage}</p>
            ) : null}

            {showResults && !loadingPatients && !loadingDocuments && !errorMessage
              ? documents.map((document) => (
                  <a
                    key={`document-${document.id}`}
                    href={document.documentPath}
                    target={document.documentTarget || undefined}
                    rel={document.documentRel || undefined}
                    role="option"
                    aria-selected="false"
                    className="block border-t border-neutral-200 px-3 py-3 first:border-t-0 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-neutral-950 dark:text-white">{document.title}</p>
                        <p className="mt-1 truncate text-xs text-neutral-500 dark:text-neutral-400">{documentMeta(document)}</p>
                      </div>

                      <span className="shrink-0 whitespace-nowrap rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200">
                        {documentChipLabel(document)}
                      </span>
                    </div>

                    <p className="mt-1 whitespace-pre-line break-words text-sm leading-5 text-neutral-600 dark:text-neutral-300">
                      {documentSecondaryText(document)}
                    </p>
                  </a>
                ))
              : null}

            {showResults && !loadingPatients && !errorMessage && documentSearchPath ? (
              <a
                href={documentSearchPath}
                role="option"
                aria-selected="false"
                className="block border-t border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
              >
                Open full document search
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
